import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS, STATUS_META, AUDIENCE_LABELS, LINK_LABELS, catLabel, fmtDate, fmtDateTime, statusMeta } from "./docConstants";
import { StatusBadge } from "./DocBits";
import SigningPanel from "./SigningPanel";
import RemindNowDialog from "./RemindNowDialog";
import RemindersTrail from "./RemindersTrail";

const AUDIENCES = ["client", "family_member", "professional_guest", "partner_vendor", "temporary_guest", "meeting_participant"];
const humanAction = (a) => a.replace(/^doc_center_|^ext_|^room_/, "").replace(/_/g, " ").replace("document ", "");

const Field = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-[#F1F5F8] last:border-0">
    <span className="text-[12px] text-[#6B7280] shrink-0">{label}</span>
    <span className="text-[12.5px] text-[#252221] text-right font-medium min-w-0">{children ?? "—"}</span>
  </div>
);

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState({ url: null, type: null, loading: false });
  const [internalNote, setInternalNote] = useState("");
  const [externalResp, setExternalResp] = useState("");
  const [busy, setBusy] = useState(false);
  const [remKey, setRemKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/doc-center/documents/${id}`);
      setDoc(data.document);
      setInternalNote(data.document.staff_review_note || "");
      setExternalResp(data.document.external_response || "");
      const h = await api.get(`/doc-center/documents/${id}/access-history`);
      setHistory(h.data.history);
    } catch (e) { toast.error(errMsg(e)); navigate("/documents"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/doc-center/documents/${id}`);
      setDoc(data.document);
      const h = await api.get(`/doc-center/documents/${id}/access-history`);
      setHistory(h.data.history);
    } catch { /* */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const loadPreview = async () => {
    if (!doc?.has_file) return;
    setPreview((p) => ({ ...p, loading: true }));
    try {
      const res = await api.get(`/doc-center/documents/${id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      setPreview({ url, type: res.data.type, loading: false });
    } catch (e) { toast.error(errMsg(e)); setPreview({ url: null, type: null, loading: false }); }
  };

  const patch = async (payload, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/doc-center/documents/${id}`, payload);
      setDoc(data.document);
      if (msg) toast.success(msg);
      const h = await api.get(`/doc-center/documents/${id}/access-history`);
      setHistory(h.data.history);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/doc-center/documents/${id}/${doc.archived ? "restore" : "archive"}`);
      toast.success(doc.archived ? "Restored." : "Archived.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (loading || !doc) return <div className="p-10 text-center text-[#6B7280]"><Icons.Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>;

  const labels = doc.link_labels || {};
  const people = doc.people || {};
  const vis = doc.visibility || [];

  return (
    <div className="pb-12" data-testid="document-detail-page">
      <button onClick={() => navigate("/documents")} className="text-[13px] text-[#1C4358] hover:underline flex items-center gap-1 mb-3" data-testid="doc-back">
        <Icons.ChevronLeft size={15} /> Document Center
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-xl font-bold text-[#1C4358] truncate">{doc.title}</h1>
            <StatusBadge status={doc.status} testid="detail-status" />
            {doc.archived && <span className="text-[11px] font-semibold text-[#94A3B8]">Archived</span>}
          </div>
          <div className="text-[13px] text-[#6B7280] mt-0.5">{catLabel(doc.category)}</div>
        </div>
        <div className="flex items-center gap-2">
          <RemindNowDialog docId={id} className="h-9 border-[#D6E0E7] text-[#1C4358] gap-1" onSent={() => { refresh(); setRemKey((k) => k + 1); }} />
          <Button variant="outline" size="sm" className="h-9 border-[#D6E0E7] text-[#1C4358] gap-1" disabled={!doc.has_file} data-testid="detail-download"
            onClick={() => downloadFile(`/doc-center/documents/${id}/download`, doc.filename || doc.title)}><Icons.Download size={15} /> Download</Button>
          <Button variant="outline" size="sm" className="h-9 border-[#D6E0E7] text-[#6B7280] gap-1" data-testid="detail-archive" onClick={doArchive} disabled={busy}>
            {doc.archived ? <><Icons.ArchiveRestore size={15} /> Restore</> : <><Icons.Archive size={15} /> Archive</>}</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-4">
        {/* LEFT: preview + review */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[13px] font-bold text-[#1C4358]">Preview</h2>
              {doc.has_file && !preview.url && <Button size="sm" variant="ghost" className="h-7 text-[12px] text-[#1C4358]" data-testid="detail-load-preview" onClick={loadPreview}>{preview.loading ? <Icons.Loader2 className="animate-spin" size={14} /> : "Load preview"}</Button>}
            </div>
            {!doc.has_file ? (
              <div className="py-8 text-center text-[13px] text-[#94A3B8]" data-testid="detail-no-file"><Icons.FileClock size={22} className="inline mb-1" /><br />No file uploaded yet {doc.status === "requested" && "— this is a pending request."}</div>
            ) : preview.url ? (
              (preview.type || "").startsWith("image/") ? (
                <img src={preview.url} alt={doc.title} className="max-h-[420px] w-full object-contain rounded-lg border border-[#EEF2F5]" data-testid="detail-preview-img" />
              ) : (preview.type || "").includes("pdf") ? (
                <iframe src={preview.url} title="preview" className="w-full h-[420px] rounded-lg border border-[#EEF2F5]" data-testid="detail-preview-pdf" />
              ) : (
                <div className="py-8 text-center text-[13px] text-[#6B7280]" data-testid="detail-preview-unsupported">Preview not supported for this file type. Use Download.</div>
              )
            ) : (
              <div className="py-8 text-center text-[13px] text-[#94A3B8]">Click "Load preview" to view the file.</div>
            )}
          </div>

          {/* Review actions */}
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4 space-y-3" data-testid="detail-review">
            <h2 className="text-[13px] font-bold text-[#1C4358]">Review & respond</h2>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="h-8 bg-[#2F7D5A] hover:bg-[#276a4c] text-white text-[12px]" data-testid="detail-mark-reviewed" disabled={busy} onClick={() => patch({ status: "approved" }, "Marked reviewed / approved.")}><Icons.CheckCircle2 size={14} className="mr-1" /> Mark reviewed</Button>
              <Button size="sm" variant="outline" className="h-8 border-[#9C1E22]/40 text-[#9C1E22] text-[12px]" data-testid="detail-needs-clarification" disabled={busy} onClick={() => patch({ status: "needs_clarification" }, "Marked needs clarification.")}><Icons.HelpCircle size={14} className="mr-1" /> Needs clarification</Button>
              <Button size="sm" variant="outline" className="h-8 border-[#7C3AED]/40 text-[#7C3AED] text-[12px]" data-testid="detail-professional-review" disabled={busy} onClick={() => patch({ category: "legal_professional_review", status: "waiting_for_review" }, "Sent to professional review.")}><Icons.Scale size={14} className="mr-1" /> Professional review</Button>
            </div>
            <div>
              <Label className="text-[12px] flex items-center gap-1 text-[#9C1E22]"><Icons.Lock size={11} /> Internal note (staff only)</Label>
              <Textarea rows={2} value={internalNote} onChange={(e) => setInternalNote(e.target.value)} className="text-[13px]" data-testid="detail-internal-note" placeholder="Only staff can see this." />
              <Button size="sm" variant="ghost" className="h-7 mt-1 text-[12px] text-[#1C4358]" data-testid="detail-save-internal" disabled={busy} onClick={() => patch({ staff_review_note: internalNote }, "Internal note saved.")}>Save internal note</Button>
            </div>
            <div>
              <Label className="text-[12px] flex items-center gap-1 text-[#2C6480]"><Icons.MessageSquare size={11} /> External-visible response</Label>
              <Textarea rows={2} value={externalResp} onChange={(e) => setExternalResp(e.target.value)} className="text-[13px]" data-testid="detail-external-response" placeholder="Visible to the client / external user." />
              <Button size="sm" variant="ghost" className="h-7 mt-1 text-[12px] text-[#1C4358]" data-testid="detail-save-external" disabled={busy} onClick={() => patch({ external_response: externalResp }, "Response saved.")}>Save response</Button>
            </div>
          </div>

          {/* Signing coordination */}
          <SigningPanel docId={id} onChanged={refresh} />

          {/* Reminders sent trail (13G) */}
          <RemindersTrail docId={id} reloadToken={remKey} />

          {/* Access history */}
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid="detail-access-history">
            <h2 className="text-[13px] font-bold text-[#1C4358] mb-2">Access history & audit</h2>
            {!history.length ? <div className="text-[12px] text-[#94A3B8]">No activity yet.</div> : (
              <ul className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {history.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px]">
                    <Icons.Dot size={16} className="text-[#1C4358] shrink-0 mt-0.5" />
                    <span className="text-[#4B5563]"><span className="font-medium text-[#252221] capitalize">{humanAction(e.action)}</span>{" · "}{e.user_email || "system"}{e.account_type ? ` (${e.account_type})` : ""}{" · "}<span className="text-[#94A3B8]">{fmtDateTime(e.at)}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* RIGHT: properties + controls */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4">
            <h2 className="text-[13px] font-bold text-[#1C4358] mb-1">Details</h2>
            <Field label="Category">
              <Select value={doc.category} onValueChange={(v) => patch({ category: v }, "Category updated.")}>
                <SelectTrigger className="h-7 w-[180px] text-[12px]" data-testid="detail-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={doc.status} onValueChange={(v) => patch({ status: v }, "Status updated.")}>
                <SelectTrigger className="h-7 w-[180px] text-[12px]" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Owner">{people.owner_name || "—"}</Field>
            <Field label="Uploaded by">{doc.uploaded_by_name || "—"}</Field>
            <Field label="Reviewer">{people.reviewer_name || "—"}</Field>
            {doc.requested_from_kind && <Field label="Requested from">{people.requested_from_name || doc.requested_from_kind.replace(/_/g, " ")}</Field>}
            {doc.due_date && <Field label="Due date">{fmtDate(doc.due_date)}</Field>}
            <Field label="Uploaded / created">{fmtDate(doc.created_at)}</Field>
            <Field label="Last updated">{fmtDate(doc.updated_at)}</Field>
            <Field label="Views">{doc.view_count || 0}</Field>
          </div>

          {/* Visibility / sharing */}
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid="detail-visibility">
            <h2 className="text-[13px] font-bold text-[#1C4358] mb-1">Visibility & sharing</h2>
            <p className="text-[11px] text-[#6B7280] mb-2">{vis.length ? "Shared with:" : "Internal only — not shared with anyone yet."}</p>
            <div className="space-y-1.5">
              {AUDIENCES.map((aud) => (
                <label key={aud} className="flex items-center justify-between gap-2 py-1">
                  <span className="text-[12.5px] text-[#252221]">{AUDIENCE_LABELS[aud]}</span>
                  <Switch checked={vis.includes(aud)} disabled={busy} data-testid={`detail-vis-${aud}`}
                    onCheckedChange={(on) => {
                      const nv = on ? [...vis, aud] : vis.filter((x) => x !== aud);
                      patch({ visibility: nv }, "Visibility updated.");
                    }} />
                </label>
              ))}
            </div>
          </div>

          {/* Linked records */}
          <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid="detail-links">
            <h2 className="text-[13px] font-bold text-[#1C4358] mb-2">Linked records</h2>
            {Object.keys(LINK_LABELS).filter((k) => (doc.links || {})[k === "client" ? "client_user_id" : k === "external_account" ? "external_account_id" : `${k}_id`]).length === 0 ? (
              <div className="text-[12px] text-[#94A3B8]">Not linked to any record.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(LINK_LABELS).map((k) => {
                  const idKey = k === "client" ? "client_user_id" : k === "external_account" ? "external_account_id" : `${k}_id`;
                  if (!(doc.links || {})[idKey]) return null;
                  return <span key={k} className="inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium bg-[#F4F8FB] text-[#1C4358] border border-[#E6EDF2]">{LINK_LABELS[k]}{labels[k] ? `: ${labels[k]}` : ""}</span>;
                })}
                {doc.links?.service_lane && <span className="inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium bg-[#F4F8FB] text-[#1C4358] border border-[#E6EDF2]">Lane: {doc.links.service_lane}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
